/**
 * FixWithAI - Phase 8: Fix with AI Integration
 * 
 * Provides UI for initiating and monitoring AI-assisted fix runs from Finding detail.
 * 
 * Architecture:
 * - FixWithAIButton: Entry point with eligibility check
 * - FixRunDialog: Confirmation + lifecycle UI
 * - FixRunStatusCard: Compact status display with timeline
 * 
 * Security:
 * - No secrets exposed in UI or API responses
 * - Clear user communication about what Fix with AI does (NOT auto-deploy)
 * - Proper error handling and retry only when backend state permits
 * 
 * Accessibility:
 * - Semantic buttons/dialogs with ARIA labels
 * - Keyboard navigation (Escape to cancel confirmation)
 * - ARIA live regions for status updates
 * - Visible focus indicators
 * - No color-only state meaning
 */

import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  api, 
  FixRunCreateRequest, 
  FixRunStatusResponse,
  FixRunStatus,
  FixStrategy,
  FindingDetailsResponse,
} from '../lib/api'
import { Button } from './design-system/Button'
import { Modal } from './design-system/Modal'
import { LoadingSpinner } from './LoadingSpinner'
import { useLocale } from '../contexts/LocaleContext'

interface FixWithAIButtonProps {
  finding: FindingDetailsResponse
  onFixRunCreated?: (fixRunId: string) => void
}

/**
 * FixWithAIButton - Entry point for Fix with AI workflow.
 * 
 * Shows button only when finding is eligible (has audit context and remediation guidance).
 * Opens confirmation dialog before creating fix run.
 */
export function FixWithAIButton({ finding, onFixRunCreated }: FixWithAIButtonProps) {
  const [showDialog, setShowDialog] = useState(false)
  const { t } = useLocale()
  
  // Eligibility: finding must have audit context and remediation guidance
  const isEligible = finding.audit_context && finding.remediation_guidance
  
  if (!isEligible) {
    return null
  }
  
  return (
    <>
      <Button
        onClick={() => setShowDialog(true)}
        variant="primary"
        size="sm"
        aria-label={t('fixAi.aria')}
      >
        {t('fixAi.title')}
      </Button>
      
      {showDialog && (
        <FixRunDialog
          finding={finding}
          onClose={() => setShowDialog(false)}
          onFixRunCreated={onFixRunCreated}
        />
      )}
    </>
  )
}

interface FixRunDialogProps {
  finding: FindingDetailsResponse
  onClose: () => void
  onFixRunCreated?: (fixRunId: string) => void
}

/**
 * FixRunDialog - Confirmation + lifecycle UI for Fix with AI.
 * 
 * Workflow:
 * 1. Show confirmation with clear explanation
 * 2. Create fix run via API
 * 3. Show lifecycle status with polling
 * 4. Handle errors and allow retry when appropriate
 */
function FixRunDialog({ finding, onClose, onFixRunCreated }: FixRunDialogProps) {
  const { t } = useLocale()
  const [stage, setStage] = useState<'confirm' | 'creating' | 'active' | 'error'>('confirm')
  const [fixRun, setFixRun] = useState<FixRunStatusResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  
  // Configuration (Phase 8: simple defaults, expandable in future phases)
  // Extract short identifier from finding ID (e.g., "finding-123" -> "finding-1")
  const shortId = finding.id.length > 8 ? finding.id.substring(0, 9) : finding.id
  const [config] = useState<FixRunCreateRequest>({
    finding_ids: [finding.id],
    target_branch: `fix/${shortId}-automated`,
    base_ref: 'main',
    auto_commit: true,
    fix_strategy: 'conservative' as FixStrategy,
    max_fixes: 1,
    timeout_seconds: 1800,
  })
  
  const handleConfirm = async () => {
    if (!finding.audit_run_id) {
      setError(t('fixAi.noContext'))
      return
    }
    
    try {
      setStage('creating')
      setError(null)
      
      const response = await api.createFixRun(finding.audit_run_id, config)
      
      // Load full status
      const status = await api.getFixRunStatus(response.fix_run_id)
      setFixRun(status)
      setStage('active')
      
      if (onFixRunCreated) {
        onFixRunCreated(response.fix_run_id)
      }
    } catch (err) {
      console.error('Failed to create fix run:', err)
      setError(err instanceof Error ? err.message : t('fixAi.createFailed'))
      setStage('error')
    }
  }
  
  const handleClose = () => {
    // Only allow close if not actively creating
    if (stage !== 'creating') {
      onClose()
    }
  }
  
  const handleRetry = () => {
    setStage('confirm')
    setError(null)
    setFixRun(null)
  }
  
  return (
    <Modal
      isOpen
      onClose={handleClose}
      title={t('fixAi.title')}
      size="lg"
    >
      {stage === 'confirm' && (
        <ConfirmationView
          finding={finding}
          config={config}
          onConfirm={handleConfirm}
          onCancel={handleClose}
        />
      )}
      
      {stage === 'creating' && (
        <div className="py-8 text-center">
          <LoadingSpinner message={t('fixAi.creating')} />
        </div>
      )}
      
      {stage === 'active' && fixRun && (
        <FixRunStatusView
          fixRun={fixRun}
          finding={finding}
          onClose={handleClose}
        />
      )}
      
      {stage === 'error' && (
        <ErrorView
          error={error || t('fixAi.unknown')}
          onRetry={handleRetry}
          onClose={handleClose}
        />
      )}
    </Modal>
  )
}

interface ConfirmationViewProps {
  finding: FindingDetailsResponse
  config: FixRunCreateRequest
  onConfirm: () => void
  onCancel: () => void
}

/**
 * ConfirmationView - Show user what Fix with AI will do before starting.
 */
function ConfirmationView({ finding, config, onConfirm, onCancel }: ConfirmationViewProps) {
  const { t } = useLocale()
  return (
    <div className="space-y-4">
      <div className="p-4 bg-[var(--bg-secondary)] rounded">
        <h3 className="text-sm font-medium mb-2">{t('fixAi.whatHappens')}</h3>
        <ul className="text-sm space-y-2 list-disc list-inside text-[var(--text-secondary)]">
          <li>{t('fixAi.createBranch', { branch: config.target_branch })}</li>
          <li>{t('fixAi.apply')}</li>
          <li>{t('fixAi.tests')}</li>
          <li>{t('fixAi.commit')}</li>
        </ul>
      </div>
      
      <div className="p-4 border-2 border-[var(--warning)] rounded bg-[var(--warning)]/5">
        <h3 className="text-sm font-medium mb-2 text-[var(--warning)]">{t('fixAi.important')}</h3>
        <p className="text-sm text-[var(--text-secondary)]">
          {t('fixAi.noDeploy')}
        </p>
      </div>
      
      <div className="space-y-2 text-sm">
        <div>
          <span className="text-[var(--text-secondary)]">{t('fixAi.finding')} </span>
          <span className="font-medium">{finding.title}</span>
        </div>
        <div>
          <span className="text-[var(--text-secondary)]">{t('fixAi.severity')} </span>
          <span className="font-medium capitalize">{finding.severity}</span>
        </div>
        <div>
          <span className="text-[var(--text-secondary)]">{t('fixAi.strategy')} </span>
          <span className="font-medium capitalize">{config.fix_strategy}</span>
          <span className="text-[var(--text-tertiary)] ml-2 text-xs">{t('fixAi.safest')}</span>
        </div>
      </div>
      
      <div className="flex justify-end gap-3 pt-4 border-t border-[var(--border)]">
        <Button
          onClick={onCancel}
          variant="secondary"
          size="md"
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          variant="primary"
          size="md"
          aria-label={t('fixAi.startAria')}
        >
          {t('fixAi.startRun')}
        </Button>
      </div>
    </div>
  )
}

interface FixRunStatusViewProps {
  fixRun: FixRunStatusResponse
  finding: FindingDetailsResponse
  onClose: () => void
}

/**
 * FixRunStatusView - Live status display with polling.
 */
function FixRunStatusView({ fixRun: initialFixRun, finding, onClose }: FixRunStatusViewProps) {
  const { t } = useLocale()
  const navigate = useNavigate()
  const [fixRun, setFixRun] = useState(initialFixRun)
  const [cancelling, setCancelling] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const pollingIntervalRef = useRef<number | null>(null)
  
  const isTerminal = ['completed', 'partial', 'failed', 'cancelled', 'timeout'].includes(fixRun.status)
  const canCancel = ['pending', 'running'].includes(fixRun.status) && !cancelling
  
  // Poll for status updates
  useEffect(() => {
    if (isTerminal) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
      return
    }
    
    const pollStatus = async () => {
      try {
        const updated = await api.getFixRunStatus(fixRun.fix_run_id)
        setFixRun(updated)
      } catch (err) {
        console.error('Failed to poll fix run status:', err)
        // Don't stop polling on transient errors
      }
    }
    
    // Poll every 5 seconds while active
    pollingIntervalRef.current = setInterval(pollStatus, 5000)
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [fixRun.fix_run_id, isTerminal])
  
  const handleCancel = async () => {
    try {
      setCancelling(true)
      setCancelError(null)
      await api.cancelFixRun(fixRun.fix_run_id)
      
      // Refresh status
      const updated = await api.getFixRunStatus(fixRun.fix_run_id)
      setFixRun(updated)
    } catch (err) {
      console.error('Failed to cancel fix run:', err)
      setCancelError(err instanceof Error ? err.message : t('fixAi.cancelFailed'))
    } finally {
      setCancelling(false)
    }
  }
  
  const handleViewAudit = () => {
    navigate(`/audit/${fixRun.audit_run_id}`)
    onClose()
  }
  
  const handleViewFinding = () => {
    navigate(`/finding/${finding.id}`)
    onClose()
  }
  
  return (
    <div className="space-y-4">
      <FixRunStatusCard fixRun={fixRun} />
      
      {cancelError && (
        <div className="p-3 bg-[var(--error)]/10 border border-[var(--error)] rounded text-sm">
          <strong>Cancel failed:</strong> {cancelError}
        </div>
      )}
      
      <div className="flex justify-between items-center pt-4 border-t border-[var(--border)]">
        <div className="flex gap-2">
          <Button
            onClick={handleViewFinding}
            variant="secondary"
            size="sm"
          >
            {t('fixAi.backFinding')}
          </Button>
          <Button
            onClick={handleViewAudit}
            variant="secondary"
            size="sm"
          >
            {t('fixAi.viewAudit')}
          </Button>
        </div>
        
        <div className="flex gap-2">
          {canCancel && (
            <Button
              onClick={handleCancel}
              variant="danger"
              size="sm"
              disabled={cancelling}
            >
              {cancelling ? t('fixAi.cancelling') : t('fixAi.cancelRun')}
            </Button>
          )}
          
          {isTerminal && (
            <Button
              onClick={onClose}
              variant="primary"
              size="sm"
            >
              {t('fixAi.done')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

interface ErrorViewProps {
  error: string
  onRetry: () => void
  onClose: () => void
}

/**
 * ErrorView - Show error and allow retry if appropriate.
 */
function ErrorView({ error, onRetry, onClose }: ErrorViewProps) {
  const { t } = useLocale()
  const canRetry = !error.includes('not available') && !error.includes('not found')
  
  return (
    <div className="space-y-4">
      <div className="p-4 bg-[var(--error)]/10 border-2 border-[var(--error)] rounded">
        <h3 className="text-sm font-medium mb-2 text-[var(--error)]">{t('fixAi.errorTitle')}</h3>
        <p className="text-sm">{error}</p>
      </div>
      
      {error.includes('not available') && (
        <div className="p-4 bg-[var(--bg-secondary)] rounded text-sm">
          <p className="mb-2">{t('fixAi.unavailable')}</p>
          <p className="text-[var(--text-secondary)]">
            {t('fixAi.unavailableHint')}
          </p>
        </div>
      )}
      
      <div className="flex justify-end gap-3 pt-4 border-t border-[var(--border)]">
        <Button
          onClick={onClose}
          variant="secondary"
          size="md"
        >
          {t('common.close')}
        </Button>
        {canRetry && (
          <Button
            onClick={onRetry}
            variant="primary"
            size="md"
          >
            {t('common.retry')}
          </Button>
        )}
      </div>
    </div>
  )
}

interface FixRunStatusCardProps {
  fixRun: FixRunStatusResponse
}

/**
 * FixRunStatusCard - Compact status display with timeline.
 * 
 * Shows current status, timestamps, progress, and results.
 */
export function FixRunStatusCard({ fixRun }: FixRunStatusCardProps) {
  const { t } = useLocale()
  const statusColor = getStatusColor(fixRun.status)
  const statusIcon = getStatusIcon(fixRun.status)
  
  return (
    <div
      className="space-y-4"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {/* Status Header */}
      <div className="flex items-center gap-3">
        <span className="text-2xl" role="img" aria-label={t('fixAi.statusAria', { status: fixRun.status })}>
          {statusIcon}
        </span>
        <div>
          <div className="flex items-center gap-2">
            <span
              className="font-medium capitalize"
              style={{ color: `var(${statusColor})` }}
            >
              {fixRun.status}
            </span>
            {!['completed', 'partial', 'failed', 'cancelled', 'timeout'].includes(fixRun.status) && (
              <span className="text-sm text-[var(--text-tertiary)]">{t('fixAi.inProgress')}</span>
            )}
          </div>
          <div className="text-sm text-[var(--text-secondary)]">
            {t('fixAi.branchLabel')} <code className="text-xs">{fixRun.target_branch}</code>
          </div>
        </div>
      </div>
      
      {/* Timeline */}
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-[var(--text-secondary)]">{t('fixAi.created')}</span>
          <span>{new Date(fixRun.created_at).toLocaleString()}</span>
        </div>
        
        {fixRun.started_at && (
          <div className="flex justify-between">
            <span className="text-[var(--text-secondary)]">{t('fixAi.started')}</span>
            <span>{new Date(fixRun.started_at).toLocaleString()}</span>
          </div>
        )}
        
        {fixRun.completed_at && (
          <div className="flex justify-between">
            <span className="text-[var(--text-secondary)]">{t('fixAi.completed')}</span>
            <span>{new Date(fixRun.completed_at).toLocaleString()}</span>
          </div>
        )}
      </div>
      
      {/* Results */}
      {(fixRun.fixes_applied > 0 || fixRun.fixes_failed > 0) && (
        <div className="p-3 bg-[var(--bg-secondary)] rounded space-y-2">
          <h4 className="text-sm font-medium">{t('fixAi.results')}</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-[var(--text-secondary)]">{t('fixAi.fixesApplied')} </span>
              <span className="font-medium text-[var(--success)]">{fixRun.fixes_applied}</span>
            </div>
            <div>
              <span className="text-[var(--text-secondary)]">{t('fixAi.failedCount')} </span>
              <span className="font-medium text-[var(--error)]">{fixRun.fixes_failed}</span>
            </div>
          </div>
          
          {fixRun.final_commit && (
            <div className="pt-2 border-t border-[var(--border)]">
              <span className="text-[var(--text-secondary)]">{t('fixAi.finalCommit')} </span>
              <code className="text-xs">{fixRun.final_commit.substring(0, 8)}</code>
            </div>
          )}
        </div>
      )}
      
      {/* Error Message */}
      {fixRun.error_message && (
        <div className="p-3 bg-[var(--error)]/10 border border-[var(--error)] rounded">
          <h4 className="text-sm font-medium mb-1 text-[var(--error)]">{t('common.error')}:</h4>
          <p className="text-sm">{fixRun.error_message}</p>
        </div>
      )}
    </div>
  )
}

function getStatusColor(status: FixRunStatus): string {
  switch (status) {
    case 'completed':
    case 'partial':
      return '--success'
    case 'pending':
    case 'running':
      return '--accent-primary'
    case 'failed':
    case 'timeout':
      return '--error'
    case 'cancelled':
      return '--text-secondary'
    default:
      return '--text-primary'
  }
}

function getStatusIcon(status: FixRunStatus): string {
  switch (status) {
    case 'completed':
      return '✅'
    case 'partial':
      return '⚠️'
    case 'pending':
      return '⏳'
    case 'running':
      return '🔄'
    case 'failed':
      return '❌'
    case 'cancelled':
      return '🚫'
    case 'timeout':
      return '⏰'
    default:
      return '❓'
  }
}
